import React, { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import { Sparkles, RefreshCw, X, Plus, Database, AlertTriangle, Cpu, Gauge } from "lucide-react";
import apiClient from "../../utils/apiClient";
import { AppModal, AppButton } from "../ui";

/**
 * GenerateFlowModal — writes a numbered-menu flow from a one-line goal plus the
 * institute's own catalogue, or revises an existing one from an instruction.
 *
 * Both modes live here rather than in two components because they differ only
 * in the brief: the institute-context panel, the token meter and the
 * nothing-is-saved guarantee are identical, and duplicating them is how the two
 * would drift apart. Pass `existingFlow` to get revision mode.
 *
 * Neither mode saves. The result opens in the normal flow editor, disabled, so
 * the admin reads every message before a customer can. That is also why the
 * "what gets sent" counts are shown up front rather than buried in a tooltip:
 * the courses and knowledge base going into the prompt are the whole reason the
 * output is worth anything, and the admin should know it before spending tokens.
 *
 * The MODEL is a visible choice here, unlike everywhere else in the product,
 * because this is the one authoring job where it decides the outcome. A thirty
 * step branching menu is a different task from answering one student's doubt,
 * and the institute-wide "AI model" setting is tuned for the latter — so the
 * server defaults this to the best model on the list and the admin picks down
 * from it when they want speed or a smaller bill.
 */

// ── Class strings ─────────────────────────────────────────────────────────
// Converted to Tailwind 2026-08-25 (Phase 5); generate-flow-modal.scss deleted
// with it. It was mostly tokenised already, so the work was the dead vocabulary:
// eight `var(--text-tertiary, #9ca3af)` fallbacks, two `var(--border-color, …)`,
// three `var(--elevate-1, …)` and three `var(--brand-primary, #7c3aed)` — the
// last of which only started resolving at all when `--brand-primary` was
// promoted out of `.demo-public` earlier today.
//
// The note and progress panels wrote their own washes — `rgba(59,130,246,0.08)`
// with a `#3b82f6` icon (a blue, on a violet app) and `rgba(124,58,237,0.08)`.
// They are `--info` and `--primary` now, so both follow the theme.
//
// ⚠️ `gfl-modal` was passed to AppModal's `className` and styled NOWHERE. That is
// the SECOND dead modal class this phase, after `.gaiq-modal` — both on an AI
// authoring modal, both a class someone added expecting the sheet to grow a rule
// for it. Worth a grep next time one is written.
//
// The `gfl-courses` / `gfl-batches` / `gfl-kb` / `gfl-actions` names that survive
// are form-control **ids**, not classes. Do not "clean them up".
const FIELD = "tw-flex tw-flex-col tw-gap-[3px]";
const LABEL =
  "tw-text-[0.74rem] tw-font-semibold tw-text-content-muted " +
  "[&_svg]:tw-me-[3px] [&_svg]:tw-align-[-1px] [&_svg]:tw-text-content-subtle";
const HINT =
  "tw-flex tw-items-center tw-gap-1 tw-text-[0.7rem] tw-leading-[1.45] tw-text-content-subtle " +
  "[&_svg]:tw-shrink-0";

const SUGGESTION =
  "tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-1 tw-rounded-[13px] tw-border " +
  "tw-border-dashed tw-border-line tw-bg-transparent tw-px-2.5 tw-py-1 tw-text-[0.72rem] " +
  "tw-text-content-muted [&_svg]:tw-shrink-0 [&_svg]:tw-text-content-subtle " +
  "hover:tw-border-solid hover:tw-border-primary hover:tw-text-content";

const CONTEXT =
  "tw-rounded-sm tw-border tw-border-line tw-bg-[var(--elevate-1)] tw-p-3";
const CONTEXT_HEAD =
  "tw-mb-2 tw-flex tw-items-center tw-gap-1.5 tw-text-[0.76rem] tw-font-semibold " +
  "tw-text-content-muted [&_svg]:tw-text-content-subtle";

const CHIP =
  "tw-inline-flex tw-items-center tw-gap-[5px] tw-rounded-xl tw-bg-[var(--elevate-1)] " +
  "tw-py-[3px] tw-pe-1.5 tw-ps-[9px] tw-text-[0.74rem] " +
  "[&_button]:tw-flex [&_button]:tw-cursor-pointer [&_button]:tw-items-center [&_button]:tw-border-0 " +
  "[&_button]:tw-bg-transparent [&_button]:tw-p-0 [&_button]:tw-text-content-subtle " +
  "[&_button:hover]:tw-text-danger";

const PANEL =
  "tw-flex tw-items-start tw-gap-[7px] tw-rounded-sm tw-px-3 tw-py-[9px] " +
  "tw-text-[0.74rem] tw-leading-[1.5] tw-text-content-muted [&_svg]:tw-mt-0.5 [&_svg]:tw-shrink-0";
const NOTE = `${PANEL} tw-bg-info/[0.08] [&_svg]:tw-text-info`;
const PROGRESS = `${PANEL} tw-bg-primary/[0.08] [&_svg]:tw-text-primary`;

const ERROR =
  "tw-rounded-sm tw-bg-danger-soft tw-px-3 tw-py-[9px] tw-text-[0.78rem] tw-leading-[1.5] tw-text-danger-fg";

const DEPTHS = [
  { value: "simple", label: "Simple", desc: "3–5 steps, one level of sub-menus" },
  { value: "standard", label: "Standard", desc: "6–9 steps, up to two levels" },
  { value: "detailed", label: "Detailed", desc: "10–16 steps, up to three levels" },
  {
    value: "comprehensive",
    label: "Comprehensive",
    desc: "18–30 steps, up to four levels — slow, and needs a high-end model",
  },
];

/* A comprehensive flow on a reasoning model is minutes, not seconds, and the
   request must outlive it — a client-side timeout after four minutes bills the
   institute for a flow nobody receives. */
const GENERATE_TIMEOUT_MS = 420000;

/* How hard a reasoning model thinks. This — not the model name — is what
   decides whether a generation takes twenty seconds or four minutes, so it is
   worded as the trade it actually is rather than as a technical setting. */
const EFFORTS = [
  { value: "low", label: "Fast", desc: "Quickest. Good for a short, flat menu" },
  { value: "medium", label: "Balanced", desc: "Noticeably better nesting for a moderate wait" },
  { value: "high", label: "Thorough", desc: "Deepest structure. Can take several minutes" },
];

const SPEED_HINT = { fast: "fast", medium: "medium", slow: "slow" };

const TRIGGER_TYPES = [
  { value: "keyword", label: "A keyword" },
  { value: "first_message", label: "A contact's first message" },
  { value: "manual", label: "Nothing — start it manually" },
];

const TONES = ["Warm and friendly", "Professional", "Concise", "Enthusiastic"];

const EXAMPLE_GOAL =
  "A menu for new leads asking about our courses, fees and how to book a free demo.";

const EXAMPLE_INSTRUCTION =
  "Add a branch for fees and payment plans, and offer to connect to a counsellor at the end.";

/* Starting points, not a fixed list — the instruction is free text. These are
   the edits admins reach for most, and one tap beats a blank box. */
const REFINE_SUGGESTIONS = [
  "Make the wording warmer and less formal",
  "Shorten every message",
  "Add a branch about fees",
  "Add a “talk to a human” option to every menu",
  "Add our upcoming batch timings",
];

const GenerateFlowModal = ({ onClose, onGenerated, existingFlow = null }) => {
  const isRefine = !!existingFlow;
  const [meta, setMeta] = useState(null);
  const [metaError, setMetaError] = useState("");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  const [elapsed, setElapsed] = useState(0);

  const [form, setForm] = useState({
    goal: "",
    extraContext: "",
    triggerType: "keyword",
    keywordDraft: "",
    keywords: [],
    depth: "standard",
    tone: TONES[0],
    language: "English",
    // Empty until ai-meta answers; the server then applies its own default, so
    // an admin who never opens the dropdown still gets the recommended model.
    model: "",
    // Empty means "let the size decide" — the server scales effort with the
    // step count, which is the right answer until someone disagrees with it.
    reasoningEffort: "",
    useActions: true,
    includeCourses: true,
    includeBatches: true,
    includeKnowledge: true,
  });

  const set = (key, value) => {
    setForm((p) => ({ ...p, [key]: value }));
    setError("");
  };

  useEffect(() => {
    let alive = true;
    apiClient
      .get("/whatsapp/flows/ai-meta")
      .then(({ data }) => {
        if (!alive) return;
        setMeta(data);
        // A source with nothing in it is switched off rather than left checked
        // and greyed — "Your courses (0)" ticked reads as a promise of content.
        const c = data.context || {};
        setForm((p) => ({
          ...p,
          model: p.model || data.defaultModel || data.models?.[0]?.id || "",
          includeCourses: p.includeCourses && c.courses > 0,
          includeBatches: p.includeBatches && c.batches > 0,
          includeKnowledge: p.includeKnowledge && c.knowledge > 0,
        }));
      })
      .catch((err) =>
        alive && setMetaError(err?.response?.data?.message || "Failed to load AI settings."),
      );
    return () => { alive = false; };
  }, []);

  /* A comprehensive flow takes minutes on a reasoning model. A spinner that
     never changes reads as a hung request, and an admin who reloads the page
     pays for a flow they will never see — so the wait is counted out loud. */
  useEffect(() => {
    if (!generating) { setElapsed(0); return undefined; }
    const started = Date.now();
    const id = setInterval(() => setElapsed(Math.round((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(id);
  }, [generating]);

  const quota = meta?.quota;
  const ctx = meta?.context;
  const models = meta?.models || [];
  const modelPickable = meta?.modelPickable !== false && models.length > 1;
  const activeModel = models.find((m) => m.id === form.model);
  /* The thinking control is meaningless on a model that does not think, and a
     dead dropdown reads as a broken one — so it only exists when it applies. */
  const showEffort = !!activeModel?.reasoning;

  const shared = {
    extraContext: form.extraContext.trim(),
    tone: form.tone,
    language: form.language.trim() || "English",
    model: form.model,
    reasoningEffort: showEffort ? form.reasoningEffort : "",
    useActions: form.useActions,
    includeCourses: form.includeCourses,
    includeBatches: form.includeBatches,
    includeKnowledge: form.includeKnowledge,
  };

  const blockedMsg = (() => {
    if (metaError) return metaError;
    if (meta && meta.enabled === false) {
      return "AI is not configured for this platform, so flows cannot be generated.";
    }
    if (quota && quota.allowed === false) {
      return quota.reason === "quota_exhausted"
        ? "Your institute has used all its AI tokens for this month."
        : "AI generation is not available on your current plan.";
    }
    return "";
  })();

  const addKeyword = () => {
    const k = form.keywordDraft.trim().toLowerCase();
    if (!k) return;
    setForm((p) => ({
      ...p,
      keywordDraft: "",
      keywords: p.keywords.includes(k) ? p.keywords : [...p.keywords, k],
    }));
  };

  const generate = async (e) => {
    e?.preventDefault();
    if (!form.goal.trim()) {
      setError(isRefine ? "Describe what should change." : "Describe what the flow should do.");
      return;
    }
    setGenerating(true);
    setError("");

    try {
      const { data } = await apiClient.post(
        isRefine ? "/whatsapp/flows/refine" : "/whatsapp/flows/generate",
        isRefine
          // The whole editor state goes up, unsaved edits included — revising
          // the saved copy would throw away what they were part-way through.
          ? { ...shared, instruction: form.goal.trim(), flow: existingFlow }
          : {
            ...shared,
            goal: form.goal.trim(),
            triggerType: form.triggerType,
            keywords: form.keywords,
            depth: form.depth,
          },
        { timeout: GENERATE_TIMEOUT_MS },
      );
      onGenerated(data.flow, data.warnings || []);
    } catch (err) {
      setError(
        err?.response?.data?.message
          || (isRefine ? "Failed to revise the flow." : "Failed to generate the flow."),
      );
      setGenerating(false);
    }
  };

  const footer = (
    <>
      <AppButton variant="outline" type="button" onClick={onClose} disabled={generating}>
        Cancel
      </AppButton>
      <AppButton
        variant="default"
        type="button"
        onClick={generate}
        disabled={generating || !!blockedMsg || !form.goal.trim() || !meta}
      >
        {generating ? (
          <>
            <RefreshCw size={14} className="tw-animate-spin" />
            {isRefine ? " Rewriting…" : " Writing the flow…"}
          </>
        ) : (
          <><Sparkles size={14} /> {isRefine ? "Rewrite" : "Generate"}</>
        )}
      </AppButton>
    </>
  );

  return (
    <AppModal
      show
      onHide={() => !generating && onClose()}
      size="lg"
      icon={<Sparkles size={18} className="tw-text-primary" />}
      title={isRefine ? "Improve this flow with AI" : "Generate a flow with AI"}
      footer={footer}
    >
      <form onSubmit={generate} className="tw-flex tw-flex-col tw-gap-3.5">
        <label className={FIELD}>
          <span className={LABEL}>
            {isRefine ? "What should change?" : "What should this flow do?"}{" "}
            <span className="tw-text-danger">*</span>
          </span>
          <Form.Control
            as="textarea"
            rows={3}
            size="sm"
            value={form.goal}
            onChange={(e) => set("goal", e.target.value)}
            placeholder={isRefine ? EXAMPLE_INSTRUCTION : EXAMPLE_GOAL}
            maxLength={2000}
            autoFocus
          />
          <span className={HINT}>
            {isRefine
              ? "The AI is given your current flow and rewrites all of it — steps you don't mention are left as they are."
              : "Describe it the way you would to a new colleague. One or two sentences is plenty."}
          </span>
        </label>

        {isRefine && (
          <div className="tw--mt-1.5 tw-flex tw-flex-wrap tw-gap-1.5">
            {REFINE_SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                className={SUGGESTION}
                onClick={() => set("goal", form.goal.trim() ? `${form.goal.trim()}. ${s}` : s)}
              >
                <Plus size={11} /> {s}
              </button>
            ))}
          </div>
        )}

        {/* ── What the AI is told about the institute ─────────────────────── */}
        <section className={CONTEXT}>
          <div className={CONTEXT_HEAD}>
            <Database size={14} />
            <span>What the AI will know about you</span>
          </div>
          <div className="tw-mb-1.5 tw-flex tw-flex-wrap tw-gap-x-[18px] tw-gap-y-1.5 tw-text-[0.78rem]">
            <Form.Check
              type="checkbox"
              id="gfl-courses"
              checked={form.includeCourses}
              onChange={(e) => set("includeCourses", e.target.checked)}
              label={`Your courses${ctx ? ` (${ctx.courses})` : ""}`}
              disabled={!!ctx && ctx.courses === 0}
            />
            <Form.Check
              type="checkbox"
              id="gfl-batches"
              checked={form.includeBatches}
              onChange={(e) => set("includeBatches", e.target.checked)}
              label={`Upcoming batches${ctx ? ` (${ctx.batches})` : ""}`}
              disabled={!!ctx && ctx.batches === 0}
            />
            <Form.Check
              type="checkbox"
              id="gfl-kb"
              checked={form.includeKnowledge}
              onChange={(e) => set("includeKnowledge", e.target.checked)}
              label={`Knowledge base${ctx ? ` (${ctx.knowledge})` : ""}`}
              disabled={!!ctx && ctx.knowledge === 0}
            />
          </div>
          <span className={HINT}>
            Only public course and batch details are sent — never instructor names, staff
            contacts or internal notes.
          </span>

          <label className={`${FIELD} tw-mt-2.5`}>
            <span className={LABEL}>Anything else it should know</span>
            <Form.Control
              as="textarea"
              rows={3}
              size="sm"
              value={form.extraContext}
              onChange={(e) => set("extraContext", e.target.value)}
              placeholder="Timings, location, admission process, common questions — anything that isn't already in your courses or knowledge base."
              maxLength={6000}
            />
          </label>
        </section>

        {/* ── Shape ──────────────────────────────────────────────────────
            Size and trigger belong to generation only. On a revision the flow
            already has both, and the editor owns them — a wording change must
            not silently repoint what starts the conversation. */}
        <div className="tw-grid tw-grid-cols-[repeat(auto-fit,minmax(200px,1fr))] tw-gap-3">
          {!isRefine && (
            <label className={FIELD}>
              <span className={LABEL}>Size</span>
              <Form.Select size="sm" value={form.depth} onChange={(e) => set("depth", e.target.value)}>
                {DEPTHS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
              </Form.Select>
              <span className={HINT}>{DEPTHS.find((d) => d.value === form.depth)?.desc}</span>
            </label>
          )}

          {!isRefine && (
            <label className={FIELD}>
              <span className={LABEL}>Starts when</span>
              <Form.Select
                size="sm"
                value={form.triggerType}
                onChange={(e) => set("triggerType", e.target.value)}
              >
                {TRIGGER_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </Form.Select>
            </label>
          )}

          <label className={FIELD}>
            <span className={LABEL}>Tone</span>
            <Form.Select size="sm" value={form.tone} onChange={(e) => set("tone", e.target.value)}>
              {TONES.map((t) => <option key={t} value={t}>{t}</option>)}
            </Form.Select>
          </label>

          <label className={FIELD}>
            <span className={LABEL}>Language</span>
            <Form.Control
              size="sm"
              value={form.language}
              onChange={(e) => set("language", e.target.value)}
              placeholder="English"
              maxLength={60}
            />
            <span className={HINT}>Every customer-facing message is written in this</span>
          </label>

          {/* The one authoring job where the model decides the outcome: a
              branching menu is a structural problem, and the smaller models
              answer it with three steps and a shrug. The wait is labelled on
              every row, because that is the trade actually being made. */}
          {modelPickable && (
            <label className={FIELD}>
              <span className={LABEL}><Cpu size={11} /> AI model</span>
              <Form.Select
                size="sm"
                value={form.model}
                onChange={(e) => set("model", e.target.value)}
              >
                {models.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}{SPEED_HINT[m.speed] ? ` — ${SPEED_HINT[m.speed]}` : ""}
                  </option>
                ))}
              </Form.Select>
              <span className={HINT}>
                {activeModel?.note || "Bigger flows need a stronger model"}
              </span>
            </label>
          )}

          {/* Not a technical setting — this is the knob that decides whether
              the admin waits twenty seconds or four minutes. */}
          {modelPickable && showEffort && (
            <label className={FIELD}>
              <span className={LABEL}><Gauge size={11} /> Thinking</span>
              <Form.Select
                size="sm"
                value={form.reasoningEffort}
                onChange={(e) => set("reasoningEffort", e.target.value)}
              >
                <option value="">Match the size ({DEPTHS.find((d) => d.value === form.depth)?.label})</option>
                {EFFORTS.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
              </Form.Select>
              <span className={HINT}>
                {EFFORTS.find((x) => x.value === form.reasoningEffort)?.desc
                  || "More thinking means better nesting and a longer wait"}
              </span>
            </label>
          )}
        </div>

        {!isRefine && form.triggerType === "keyword" && (
          <div className={FIELD}>
            <span className={LABEL}>Trigger keywords</span>
            {!!form.keywords.length && (
              <div className="tw-mb-[5px] tw-flex tw-flex-wrap tw-gap-[5px]">
                {form.keywords.map((k) => (
                  <span key={k} className={CHIP}>
                    {k}
                    <button
                      type="button"
                      onClick={() => set("keywords", form.keywords.filter((x) => x !== k))}
                    >
                      <X size={11} />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="tw-flex tw-max-w-[340px] tw-gap-1.5">
              <Form.Control
                size="sm"
                value={form.keywordDraft}
                onChange={(e) => set("keywordDraft", e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addKeyword(); } }}
                placeholder="hi, hello, menu…"
              />
              <AppButton size="sm" variant="outline" type="button" onClick={addKeyword}>Add</AppButton>
            </div>
            <span className={HINT}>Leave empty and the AI suggests them — you can edit them after</span>
          </div>
        )}

        <label className="tw-block tw-text-[0.82rem]">
          <Form.Check
            type="switch"
            id="gfl-actions"
            checked={form.useActions}
            onChange={(e) => set("useActions", e.target.checked)}
            label="Let it wire up actions (talk to a human, stop messages)"
          />
        </label>

        {generating && (
          <div className={PROGRESS}>
            <RefreshCw size={13} className="tw-animate-spin" />
            <span>
              {isRefine ? "Rewriting" : "Writing"} with {activeModel?.label || form.model || "AI"}
              {" — "}{elapsed}s.{" "}
              {activeModel?.speed === "slow" || form.reasoningEffort === "high"
                ? "This model thinks before it answers, so several minutes is normal — pick a faster one or set Thinking to Fast if that is too long."
                : "This can take a minute or two."}
              {" "}Leave this open — closing it loses the flow you are paying for.
            </span>
          </div>
        )}

        {meta && !generating && (
          <span className="tw-text-[0.72rem] tw-text-content-subtle">
            {quota?.allowed !== false && quota?.limit > 0 && (
              <>
                Institute AI tokens: {quota.used.toLocaleString()} / {quota.limit.toLocaleString()} used
                {" · "}{quota.remaining.toLocaleString()} left this month{" · "}
              </>
            )}
            {quota?.limit === -1 && <>Unlimited AI tokens · </>}
            {modelPickable
              ? <>Reasoning models cost more tokens than the fast ones</>
              : <>Model {meta.defaultModel || meta.model || "—"}</>}
          </span>
        )}

        <div className={NOTE}>
          <AlertTriangle size={13} />
          <span>
            {isRefine ? (
              <>
                Nothing is saved. The rewritten flow replaces what is in the editor — read it,
                then save it yourself. Your trigger, keywords and step names are kept.
              </>
            ) : (
              <>
                Nothing is saved and nothing goes live. The flow opens in the editor switched
                off, so you can rewrite every message before a customer sees it.
              </>
            )}
          </span>
        </div>

        {blockedMsg && <div className={ERROR}>{blockedMsg}</div>}
        {error && <div className={ERROR}>{error}</div>}
      </form>
    </AppModal>
  );
};

export default GenerateFlowModal;
